import React, { useState, useEffect, createContext, useContext } from "react";
import { toast } from "react-toastify"; // Import toast from react-toastify

const ErrorContext = createContext();

export const useError = () => useContext(ErrorContext);

const ErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState(null);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    const handleError = (event) => {
      setHasError(true);
      setError(event.error || new Error("An unexpected error occurred"));
    };

    const handleRejection = (event) => {
      setHasError(true);
      setError(event.reason || new Error("Unhandled Promise Rejection"));
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  // Function to trigger API error using toast
  const handleApiError = (message) => {
    setApiError(message);
    toast.error(message, { autoClose: 5000 }); // Display toast for 5 seconds
    setTimeout(() => setApiError(null), 5000); // Clear error after 5 seconds
  };

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-center">
        <h1 className="text-3xl font-bold text-red-600">
          Something went wrong.
        </h1>
        <p className="text-red-700 mt-2">{error?.message}</p>
        <button
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-blue-700"
          onClick={() => window.location.reload()}
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <ErrorContext.Provider value={{ handleApiError }}>
      {children}
    </ErrorContext.Provider>
  );
};

export default ErrorBoundary;
